/**
 * Conditional overlays for the visitor map experience.
 * Splash, welcome, consent, feedback, offline, install prompt, arrival modal.
 * All independent — render-if-condition patterns extracted from VisitorMapBySlug.
 */

import { OfflineIndicator } from "./OfflineIndicator";

import { InstallPrompt } from "./InstallPrompt";
import { SplashScreen } from "./SplashScreen";
import { WelcomeModal } from "./WelcomeModal";
import { ConsentBanner } from "./ConsentBanner";
import { FeedbackBanner } from "./FeedbackBanner";
import { ArrivalModal } from "./ArrivalModal";
import type { POI } from "../MapCanvas";
import type { SponsorLogo, SponsorTierGroup } from "./sponsor-types";
import type { SplashState } from "../../hooks/visitor/useVisitorSplash";
import type { UseVisitorFeedbackReturn } from "../../hooks/visitor/useVisitorFeedback";

interface VisitorOverlaysProps {
  // Splash
  showSplash: boolean;
  splashState: SplashState;
  isFromCache: boolean;
  branding: {
    logoUrl?: string | null;
    appName?: string | null;
    brandColor?: string | null;
    splashBackground?: string | null;
    hidePoweredBy?: boolean;
  } | null;
  onRetry: () => void;
  onDismissSplash: () => void;
  splashSponsors?: SponsorTierGroup[];
  welcomeSponsors?: SponsorTierGroup[];
  onSponsorImpression?: (sponsors: SponsorLogo[], placement: string) => void;

  // Welcome
  welcomeModalOpen: boolean;
  isMapReady: boolean;
  mapName: string;
  onWelcomeDismiss: () => void;
  onSponsorClick?: (sponsor: SponsorLogo, placement: string) => void;

  // Consent
  consentStatus: "pending" | "accepted" | "declined";
  consentBannerDismissed: boolean;
  onAcceptConsent: () => void;
  onDismissConsent: () => void;
  onConsentPromptShown?: () => void;

  // Feedback
  showFeedbackBanner: boolean;
  feedbackPhase: UseVisitorFeedbackReturn["feedbackPhase"];
  rating: number | null;
  comment: string;
  setRating: (r: number) => void;
  setComment: (c: string) => void;
  submitFeedback: () => void;
  skipFeedback: () => void;
  dismissFeedback: () => void;

  // Arrival
  showArrivalModal: boolean;
  arrivedPOI: POI | null;
  onArrivalClose: () => void;

  // Accent
  brandAccentPoweredBy?: string | null;
}

export function VisitorOverlays({
  showSplash,
  splashState,
  isFromCache,
  branding,
  onRetry,
  onDismissSplash,
  splashSponsors,
  welcomeSponsors,
  onSponsorImpression,
  welcomeModalOpen,
  isMapReady,
  mapName,
  onWelcomeDismiss,
  onSponsorClick,
  consentStatus,
  consentBannerDismissed,
  onAcceptConsent,
  onDismissConsent,
  onConsentPromptShown,
  showFeedbackBanner,
  feedbackPhase,
  rating,
  comment,
  setRating,
  setComment,
  submitFeedback,
  skipFeedback,
  dismissFeedback,
  showArrivalModal,
  arrivedPOI,
  onArrivalClose,
  brandAccentPoweredBy,
}: VisitorOverlaysProps) {
  return (
    <>
      {/* Arrival Modal */}
      {showArrivalModal && arrivedPOI && (
        <ArrivalModal destination={arrivedPOI} onClose={onArrivalClose} />
      )}

      {/* Offline Indicator */}
      <OfflineIndicator />

      {/* PWA Install Prompt */}
      <InstallPrompt />

      {/* Welcome Modal (first visit) */}
      {welcomeModalOpen && (
        <WelcomeModal
          mapName={mapName}
          branding={branding}
          isMapReady={isMapReady}
          onDismiss={onWelcomeDismiss}
          sponsors={welcomeSponsors}
          onSponsorClick={onSponsorClick}
          onSponsorImpression={onSponsorImpression}
        />
      )}

      {/* Analytics Consent Banner (visitors only, after welcome modal dismissed) */}
      {consentStatus === "pending" &&
        !welcomeModalOpen &&
        !consentBannerDismissed && (
          <ConsentBanner
            onAccept={onAcceptConsent}
            onDismiss={onDismissConsent}
            onShown={onConsentPromptShown}
          />
        )}

      {/* Visitor Feedback Banner (GPS-gated, 30+ min) */}
      {showFeedbackBanner && (
        <FeedbackBanner
          feedbackPhase={feedbackPhase}
          rating={rating}
          comment={comment}
          setRating={setRating}
          setComment={setComment}
          submit={submitFeedback}
          skip={skipFeedback}
          dismiss={dismissFeedback}
        />
      )}

      {/* Splash Screen Overlay */}
      {showSplash && (
        <SplashScreen
          branding={branding}
          state={splashState}
          isFromCache={isFromCache}
          onRetry={onRetry}
          onContinue={onDismissSplash}
          showPoweredBy={!branding?.hidePoweredBy}
          sponsors={splashSponsors}
          onSponsorImpression={onSponsorImpression}
          brandAccent={brandAccentPoweredBy}
        />
      )}
    </>
  );
}
